import React from "react";
import styled from "styled-components";

export const TYPE_ELEMENTS = {
  H_1: "h1",
  H_2: "h2",
  H_3: "h3",
  H_4: "h4",
  H_5: "h5",
  H_6: "h6",
  H_7: "h7",
  SANS_H7: "sansH7",
  SUBTITLE_1: "subtitle1",
  SUBTITLE_2: "subtitle2",
  CTA_LABEL: "ctaLabel",
  BUTTON: "button",
  CAPTION: "caption",
  BODY_1: "body1",
  BODY_2: "body2",
  BODY_3: "body3",
};

export default function Typography({
  typeElement,
  customWeight = "",
  customColor = "",
  isOutline = "false",
  customCase = "",
  fontSize = "",
  lineHeight = "",
  children,
}) {
  switch (typeElement) {
    case TYPE_ELEMENTS.H_1:
      return (
        <H1
          customColor={customColor}
          customCase={customCase}
          className="font-syne"
        >
          {children}
        </H1>
      );
    case TYPE_ELEMENTS.H_2:
      return (
        <H2
          customColor={customColor}
          customCase={customCase}
          className="font-syne"
        >
          {children}
        </H2>
      );
    case TYPE_ELEMENTS.H_3:
      return (
        <H3
          className="font-syne"
          isOutline={isOutline}
          customColor={customColor}
          customCase={customCase}
        >
          {children}
        </H3>
      );
    case TYPE_ELEMENTS.H_4:
      return <H4 className="font-syne">{children}</H4>;
    case TYPE_ELEMENTS.H_5:
      return (
        <H5 className="font-syne" customCase={customCase}>
          {children}
        </H5>
      );
    case TYPE_ELEMENTS.H_6:
      return (
        <H6
          className="font-syne"
          customColor={customColor}
          customWeight={customWeight}
        >
          {children}
        </H6>
      );
    case TYPE_ELEMENTS.H_7:
      return (
        <H7
          className="font-syne"
          customColor={customColor}
          customWeight={customWeight}
        >
          {children}
        </H7>
      );
    case TYPE_ELEMENTS.SANS_H7:
      return (
        <H7
          className="font-dm-sans"
          customColor={customColor}
          customWeight={customWeight}
        >
          {children}
        </H7>
      );
    case TYPE_ELEMENTS.SUBTITLE_1:
      return <Subtitle1 className="font-syne">{children}</Subtitle1>;
    case TYPE_ELEMENTS.SUBTITLE_2:
      return <Subtitle2 className="font-syne">{children}</Subtitle2>;
    case TYPE_ELEMENTS.CTA_LABEL:
      return <CTALabel className="font-dm-sans">{children}</CTALabel>; // in final design it uses prompt but no other place has propmpt usage
    case TYPE_ELEMENTS.BUTTON:
      return <Button className="font-syne">{children}</Button>;
    case TYPE_ELEMENTS.CAPTION:
      return <Caption className="font-dm-sans">{children}</Caption>;
    case TYPE_ELEMENTS.BODY_1:
      return (
        <Body1
          className="font-dm-sans"
          customColor={customColor}
          customWeight={customWeight}
        >
          {children}
        </Body1>
      );
    case TYPE_ELEMENTS.BODY_2:
      return (
        <Body2
          className="font-dm-sans"
          customColor={customColor}
          customWeight={customWeight}
        >
          {children}
        </Body2>
      );
    case TYPE_ELEMENTS.BODY_3:
      return (
        <Body3
          className="font-dm-sans"
          fontSize={fontSize}
          lineHeight={lineHeight}
          customWeight={customWeight}
        >
          {children}
        </Body3>
      );
    default:
      return <></>;
  }
}

const H1 = styled.span`
  font-size: 88px;
  font-weight: 700;
  line-height: 88px;
  letter-spacing: 1%;

  ${(props) => props.customColor !== "" && `color: ${props.customColor};`}
  ${(props) =>
    props.customCase !== "" && `text-transform: ${props.customCase};`}

  @media (max-width: 1400px) {
    font-size: 72px;
    line-height: 72px;
  }

  @media (max-width: 1280px) {
    font-size: 56px;
    line-height: 56px;
  }

  @media (max-width: 1024px) {
    font-size: 40px;
    line-height: 40px;
  }

  @media (max-width: 768px) {
    font-size: 32px;
    line-height: 32px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    line-height: 24px;
  }
`;

const H2 = styled.span`
  font-size: 64px;
  font-weight: 700;
  line-height: 64px;
  letter-spacing: 0%;

  ${(props) => props.customColor !== "" && `color: ${props.customColor};`}
  ${(props) =>
    props.customCase !== "" && `text-transform: ${props.customCase};`}

  @media (max-width: 1400px) {
    font-size: 56px;
    line-height: 56px;
  }

  @media (max-width: 1280px) {
    font-size: 48px;
    line-height: 48px;
  }

  @media (max-width: 1024px) {
    font-size: 32px;
    line-height: 36px;
  }

  @media (max-width: 768px) {
    font-size: 28px;
    line-height: 28px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    line-height: 24px;
  }
`;

const H3 = styled.span`
  font-size: 56px;
  font-weight: 700;
  line-height: 54px;
  letter-spacing: 0%;

  -webkit-text-fill-color: ${(props) =>
    props.isOutline === "true" && "transparent"};
  -webkit-text-stroke: ${(props) => props.isOutline === "true" && "1px white"};
  ${(props) =>
    props.customCase !== "" && `text-transform: ${props.customCase};`}
  ${(props) => props.customColor !== "" && `color: ${props.customColor};`}

  @media (max-width: 1400px) {
    font-size: 48px;
    line-height: 48px;
  }

  @media (max-width: 1280px) {
    font-size: 40px;
    line-height: 42px;
  }

  @media (max-width: 1024px) {
    font-size: 36px;
    line-height: 37px;
  }

  @media (max-width: 768px) {
    font-size: 32px;
    line-height: 33px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    line-height: 25px;
  }
`;

const H4 = styled.span`
  font-size: 40px;
  font-weight: 700;
  line-height: 30px;
  letter-spacing: -1.5%;

  @media (max-width: 1400px) {
    font-size: 36px;
  }

  @media (max-width: 1280px) {
    font-size: 32px;
    line-height: 28px;
  }

  @media (max-width: 1024px) {
    font-size: 28px;
    line-height: 24px;
  }

  @media (mac-width: 768px) {
    font-size: 24px;
    line-height: 20px;
  }

  @media (max-width: 480px) {
    font-size: 20px;
    line-height: 16px;
  }
`;

const H5 = styled.span`
  font-size: 32px;
  font-weight: 700;
  line-height: 30px;
  letter-spacing: -1.5%;
  ${(props) =>
    props.customCase !== "" && `text-transform: ${props.customCase};`}

  @media (max-width: 1400px) {
    font-size: 28px;
    line-height: 28px;
  }

  @media (max-width: 1280px) {
    font-size: 26px;
    line-height: 26px;
  }

  @media (max-width: 1024px) {
    font-size: 24px;
    line-height: 24px;
  }

  @media (max-width: 768px) {
    font-size: 20px;
    line-height: 20px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    line-height: 18px;
  }
`;

const H6 = styled.span`
  font-size: 24px;
  font-weight: ${(props) =>
    props.customWeight === "" ? "700" : props.customWeight};
  ${(props) => props.customColor !== "" && `color: ${props.customColor};`}
  line-height: 25px;
  letter-spacing: -1.5%;

  @media (max-width: 1280px) {
    font-size: 22px;
    line-height: 24px;
  }

  @media (max-width: 1024px) {
    font-size: 20px;
    line-height: 22px;
  }

  @media (max-width: 768px) {
    font-size: 18px;
    line-height: 20px;
  }

  @media (max-width: 640px) {
    font-size: 16px;
    line-height: 18px;
  }
`;

const H7 = styled.span`
  font-size: 20px;
  font-weight: ${(props) =>
    props.customWeight === "" ? "700" : props.customWeight};
  ${(props) => props.customColor !== "" && `color: ${props.customColor};`}
  line-height: 18px;
  letter-spacing: 1%;

  @media (max-width: 1400px) {
    font-size: 18px;
  }

  @media (max-width: 1024px) {
    font-size: 16px;
    line-height: 14px;
  }

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const Subtitle1 = styled.h1`
  font-size: 18px;
  font-weight: 600;
  line-height: 22px; // 28px as per final design
  letter-spacing: -1.5%;

  @media (max-width: 1400px) {
    font-size: 16px;
    line-height: 20px;
  }

  @media (max-width: 1280px) {
    font-size: 14px;
    line-height: 18px;
  }

  @media (max-width: 768px) {
    font-size: 14px; // taken from mobile typography
    line-height: 17px; // taken from the mobile typography
  }
`;

const Subtitle2 = styled.h2`
  font-size: 14px;
  font-weight: 600;
  line-height: 17px; // as per final design
  letter-spacing: 0.1%;

  @media (max-width: 768px) {
    font-size: 12px;
    line-height: 15px; // mobile typography says 24px but that's too large
  }

  @media (max-height: 640px) {
    line-height: 0px;
  }
`;

const CTALabel = styled.span`
  font-size: 20px;
  font-weight: 700;
  line-height: 24px; // as fer final design
  letter-spacing: 1%; // as per final design

  @media (max-width: 768px) {
    font-size: 20px; // taken from mobile typography
    line-height: 20px; // taken from mobile typography
  }

  @media (max-width: 480px) {
    font-size: 16px; // taken from mobile typography
    line-height: 16px; // taken from mobile typography
  }
`;

const Button = styled.span`
  font-size: 16px;
  font-weight: 700;
  line-height: 24px; // as fer final design
  letter-spacing: 1%; // as per final design

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 20px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const Caption = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 0px; // as per final design
  letter-spacing: 0%; // as per final design
`;

const Body1 = styled.span`
  font-size: 24px;
  line-height: 25px;
  letter-spacing: 0.5%;
  font-weight: ${(props) =>
    props.customWeight === "" ? "700" : props.customWeight};
  ${(props) => props.customColor !== "" && `color: ${props.customColor};`}

  @media (max-width: 1280px) {
    font-size: 22px;
    line-height: 21px;
  }

  @media (max-width: 1024px) {
    font-size: 20px;
  }

  @media (max-width: 768px) {
    font-size: 18px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const Body2 = styled.span`
  font-size: 16px;
  font-weight: ${(props) =>
    props.customWeight === "" ? "500" : props.customWeight};
  ${(props) => props.customColor !== "" && `color: ${props.customColor};`}
  // line-height: 25px;
  letter-spacing: -1.5%;

  @media (max-width: 480px) {
    font-size: 14px;
    // line-height: 22px;
  }
`;

const Body3 = styled.span`
  font-size: ${(props) => (props.fontSize === "" ? "14px" : props.fontSize)};
  font-weight: ${(props) =>
    props.customWeight === "" ? "400" : props.customWeight};
  line-height: ${(props) =>
    props.lineHeight === "" ? "20px" : props.lineHeight}; // as per final design
  letter-spacing: 0.25; // as per final design

  // @media (max-width: 768px) {
  //   line-height: 14px; // mobile typography says 24px but that's too large
  // }

  @media (max-width: 480px) {
    font-size: 12px;
    line-height: 16px; // mobile typography says 24px but that's too large
  }
`;
